import { useCallback, useMemo } from "react";
import {
  useAddBookmarkedVendorMutation,
  useBookmarkedVendorsQuery,
  useRemoveBookmarkedVendorMutation,
} from "../custom-hooks/queries";

/**
 * Sponsor-scoped vendor bookmarks for the Bookmarked system list.
 * Backed by GET/POST/DELETE /Sponsors/MyVendors/Bookmarks.
 */
export function useBookmarkedVendors(sponsorId = null) {
  const {
    data: bookmarkedProviderIds = [],
    isLoading,
    isError,
    refetch,
  } = useBookmarkedVendorsQuery(sponsorId);

  const addMutation = useAddBookmarkedVendorMutation(sponsorId);
  const removeMutation = useRemoveBookmarkedVendorMutation(sponsorId);

  const bookmarkedIdSet = useMemo(
    () => new Set(bookmarkedProviderIds.map(String)),
    [bookmarkedProviderIds]
  );

  const isBookmarked = useCallback(
    (providerId) => bookmarkedIdSet.has(String(providerId || "")),
    [bookmarkedIdSet]
  );

  const toggleBookmark = useCallback(
    async (providerId) => {
      const id = String(providerId || "");
      if (!id || !sponsorId) return;
      if (bookmarkedIdSet.has(id)) {
        await removeMutation.mutateAsync(id);
      } else {
        await addMutation.mutateAsync(id);
      }
    },
    [addMutation, bookmarkedIdSet, removeMutation, sponsorId]
  );

  const addBookmark = useCallback(
    async (providerId) => {
      const id = String(providerId || "");
      if (!id || !sponsorId || bookmarkedIdSet.has(id)) return;
      await addMutation.mutateAsync(id);
    },
    [addMutation, bookmarkedIdSet, sponsorId]
  );

  const addBookmarks = useCallback(
    async (providerIds = []) => {
      if (!sponsorId) return;
      const pending = providerIds
        .map((entry) => String(entry || ""))
        .filter((id) => id && !bookmarkedIdSet.has(id));
      if (!pending.length) return;
      await Promise.all(pending.map((id) => addMutation.mutateAsync(id)));
    },
    [addMutation, bookmarkedIdSet, sponsorId]
  );

  const removeBookmark = useCallback(
    async (providerId) => {
      const id = String(providerId || "");
      if (!id || !sponsorId || !bookmarkedIdSet.has(id)) return;
      await removeMutation.mutateAsync(id);
    },
    [bookmarkedIdSet, removeMutation, sponsorId]
  );

  const removeBookmarks = useCallback(
    async (providerIds = []) => {
      if (!sponsorId) return { removedCount: 0 };
      const pending = providerIds
        .map((entry) => String(entry || ""))
        .filter((id) => id && bookmarkedIdSet.has(id));
      if (!pending.length) return { removedCount: 0 };
      await Promise.all(pending.map((id) => removeMutation.mutateAsync(id)));
      return { removedCount: pending.length };
    },
    [bookmarkedIdSet, removeMutation, sponsorId]
  );

  /**
   * Ticket 1.7 — Bookmark Selected: if any selected vendor is unbookmarked,
   * bookmark all selected; otherwise unbookmark all selected.
   */
  const toggleBookmarksForSelection = useCallback(
    async (providerIds = []) => {
      if (!sponsorId) return null;
      const ids = providerIds.map((entry) => String(entry || "")).filter(Boolean);
      if (!ids.length) return null;

      const anyUnbookmarked = ids.some((id) => !bookmarkedIdSet.has(id));
      if (anyUnbookmarked) {
        const toAdd = ids.filter((id) => !bookmarkedIdSet.has(id));
        const alreadyBookmarkedCount = ids.length - toAdd.length;
        if (toAdd.length) {
          await Promise.all(toAdd.map((id) => addMutation.mutateAsync(id)));
        }
        return {
          action: "bookmarked",
          addedCount: toAdd.length,
          alreadyBookmarkedCount,
        };
      }

      const { removedCount } = await removeBookmarks(ids);
      return {
        action: "unbookmarked",
        removedCount,
      };
    },
    [addMutation, bookmarkedIdSet, removeBookmarks, sponsorId]
  );

  const isMutating = addMutation.isPending || removeMutation.isPending;

  return {
    bookmarkedProviderIds,
    isBookmarked,
    toggleBookmark,
    addBookmark,
    addBookmarks,
    removeBookmark,
    removeBookmarks,
    toggleBookmarksForSelection,
    isLoading,
    isError,
    isMutating,
    refetch,
  };
}

export default useBookmarkedVendors;
